import { isEqual, omit } from 'lodash';
import { useEffect, useReducer, useState } from 'react';

/**
 * Unique options to useAPI hook.
 * @typedef {Object} UseAPIParams
 * @property {string} url The URL to fetch from
 * @property {(data: any) => void} [onSuccess] Optional callback function to execute on success. Only use this when
 * 		being passed to the request dispatch function or, if passed to useAPI, only in cases where any called
 * 		functions/referenced values will never change (ex. setState and dispatch from useState and useReducer hooks).
 * @property {(error: Error & { response: Response, responseData }) => void} [onError] Optional callback function
 * 		to execute on error. Only use this when being passed to the request dispatch function or, if passed to useAPI,
 * 		only in cases where any called functions/referenced values will never change (ex. setState and dispatch from
 * 		useState and useReducer).
 */

/**
 * State returned by useAPI hook
 * @typedef {Object} APIState
 * @property {boolean} isLoading Indicates if a request is actively in progress.
 * @property {boolean} reqComplete Indicates if the most recent request has completed - without respect
 * 		as to whether the request failed or succeeded.
 * @property {*} data The data returned from the request.
 * @property {Error & { response: Response, responseData }} error If the request failed, the error message with the
 * 		response available in `error.response` with the decoded contents in `error.responseData`.
 */

const fetchReducer = (state, action) => {
	switch (action.type) {
		case 'FETCH_INIT':
			const newState = {
				...state,
				isLoading: true,
				isError: false,
				reqComplete: false,
			};
			// Prevents re-render if initialized as loading.
			return isEqual(newState, state) ? state : newState;
		case 'FETCH_SUCCESS':
			return {
				...state,
				isLoading: false,
				error: null,
				data: action.payload,
				reqComplete: true,
			};
		case 'FETCH_FAILURE':
			return {
				...state,
				isLoading: false,
				error: action.payload,
				reqComplete: true,
			};
		default:
			throw new Error('Invalid action for fetch reducer.');
	}
};

/**
 * Wraps the browser fetch API into a custom hook to reduce boilerplate code and to
 * prevent issues where state is updated after page navigation change.
 *
 * There are two typical usage patterns, One where `initialFetchOpts` is specified, and another
 * when it is omitted/set to null/undefined. The former case will execute a request using the provided fetch
 * options on page/component load. The latter will not execute a request on load and needs to be manually
 * triggered using the request dispatch function returned by this hook. Typically, if you have a page that needs
 * to both perform a GET and type of POST/PUT/DELETE/submit-like request, you would do the get request with a
 * call to this hook that fetches on load and you would do submit-like requests through another call to this hook
 * without any initial params.
 *
 * Request options are the same as those passed to fetch API's init param with addition of an `onSuccess` and `onError`
 * property that can be used in addition to/in place of the hook's data and error return values and that the URL is
 * specified as the `url` property of the options object. Additionally, options can be provided as a string to GET
 * from a URL relative to the app's API root.
 *
 * @param {string | UseAPIParams & RequestInit} [onLoadRequestOpts] Options for a request that will run
 * 		when the component is mounted. Omit or set to null/undefined to not execute a request on component
 * 		mount/load. Use a string for a simple GET request instead.
 * @param {*} [initialData] Optional initial value for the data returned by this hook.
 * @returns {[APIState, (reqOpts: string | UseAPIParams & RequestInit) => void]} Returns a tuple of a state object
 * 		representing the request status and results and a request dispatcher function that can be used to dispatch a
 * 		new request using the same options that are provided to the hook. A re-fetch of the returned `APIState.data`
 * 		will occur when this is called, permitting a re-fetch of the same url by passing in `{ url: originalUrl }`.
 */
const useAPI = (onLoadRequestOpts, initialData) => {
	const [fetchOpts, setFetchOpts] = useState(processUrlOpts(onLoadRequestOpts));
	const [state, dispatch] = useReducer(fetchReducer, {
		isLoading: !!onLoadRequestOpts,
		error: null,
		data: initialData,
		reqComplete: false,
	});

	useEffect(() => {
		let ignore = false;
		const controller = new AbortController();
		const signal = controller.signal;

		if (fetchOpts) {
			fetchData(fetchOpts, dispatch, signal, ignore);
			return () => {
				ignore = true;
				controller.abort();
			};
		}
	}, [fetchOpts]);

	return [
		state,
		urlOpts => {
			setFetchOpts(processUrlOpts(urlOpts));
		},
	];
};

const processUrlOpts = urlOpts => {
	if (!urlOpts) {
		return urlOpts;
	}

	if (typeof urlOpts === 'string') {
		urlOpts = { url: urlOpts };
	}

	if (urlOpts.body) {
		prepareReqBody(urlOpts);
	}

	return urlOpts;
};

const prepareReqBody = urlOpts => {
	if (urlOpts.headers instanceof Headers) {
		if (!urlOpts.headers.has('Content-Type'))
			urlOpts.headers.set('Content-Type', 'application/json');

		if (
			typeof urlOpts.body !== 'string' &&
			urlOpts.headers.get('Content-Type') === 'application/json'
		)
			urlOpts.body = JSON.stringify(urlOpts.body);
	} else {
		urlOpts.headers = {
			'Content-Type': 'application/json',
			...urlOpts.headers,
		};

		if (
			typeof urlOpts.body !== 'string' &&
			urlOpts.headers['Content-Type'] === 'application/json'
		)
			urlOpts.body = JSON.stringify(urlOpts.body);
	}
};

const fetchData = async (fetchOpts, dispatch, signal, ignore) => {
	dispatch({ type: 'FETCH_INIT' });
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_PREFIX_PROTO}${process.env.REACT_APP_API_DOMAIN}${process.env.REACT_APP_API_PREFIX}${fetchOpts.url}`,
			{ signal, ...omit(fetchOpts, 'onSuccess', 'onError') }
		);

		if (!response.ok) {
			const error = new Error('HTTP Request returned a non-OK status.');
			error.response = response;
			error.responseData = await decodeResponseBody(response);
			throw error;
		}

		const result = await decodeResponseBody(response);
		if (!ignore) {
			dispatch({ type: 'FETCH_SUCCESS', payload: result });
			if (fetchOpts.onSuccess) fetchOpts.onSuccess(result);
		}
	} catch (error) {
		if (!(error instanceof DOMException && signal.aborted)) {
			console.error(error);
			if (!ignore) {
				dispatch({ type: 'FETCH_FAILURE', payload: error });
				if (fetchOpts.onError) fetchOpts.onError(error);
			}
		}
	}
};

const decodeResponseBody = async response => {
	if (response.headers.get('Content-Length') !== '0') {
		if (response.headers.get('Content-Type').includes('application/json')) {
			return response.json();
		} else {
			return response.text();
		}
	} else {
		return null;
	}
};

export default useAPI;
